import { flash, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField, InputField, requiredNumberMoreThanZero, requiredString, TextareaField } from "common";
import { isEqual, omit, pick } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    CatalogType,
    CollectionNode,
    Coordinates,
    Membership,
    Permission,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleFilterObject,
    PurchaseRuleShortcutType,
    Seller,
} from "types";
import { boolean, object, string } from "yup";
import { getProductCollectionPayload, ProductCollectionSelector } from "../../ProductCollectionSelector";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { CurrencyInputField } from "@/components/Field/CurrencyInputField";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { LocationField } from "@/components/Product/LocationField";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { getDefaultMembership } from "@/models/Membership";

interface MembershipDescriptionFormData
    extends Pick<Membership, "name" | "desc" | "refundPolicy" | "price" | "meetingLocation" | "seller" | "currency"> {
    enableMap: boolean;
    collections?: string[];
}

interface MembershipDescriptiveContentProps {
    membership?: Membership;
    isLoading?: boolean;
}

export const MembershipDescriptiveContent = ({ membership, isLoading = false }: MembershipDescriptiveContentProps) => {
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const collectionModal = useModal();

    const { isGranted } = useAuthorization();
    const showCollectionModification = isGranted(Permission.MODIFY_COLLECTIONS);

    const { data: addedCollections, isLoading: isLoadingAddedCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        membership?.id ? { product: membership.id } : undefined,
        { enabled: !!membership?.id && showCollectionModification },
    );
    const addedCollectionPaths = addedCollections.map((node) => node.path ?? "").filter(Boolean);

    const { mutateAsync: saveCollections } = useMutation(
        async ({ newPaths, membershipId }: { newPaths: string[]; membershipId: string }) => {
            const collectionUpdateOperations = getProductCollectionPayload({
                newPaths,
                oldPaths: addedCollectionPaths,
                addedCollections,
                productId: membershipId,
            });
            if (collectionUpdateOperations.length > 0) {
                await api.patch("/collectionNodes", { ops: collectionUpdateOperations });
                invalidate("collectionNodes");
            }
        },
    );

    const { mutateAsync: save } = useMutation(async (payload: MembershipDescriptionFormData) => {
        const isExisting = !!membership?.id;
        const response = isExisting
            ? await api.put<Membership>(`/memberships/${membership.id}`, payload)
            : await api.post<Membership>("/memberships", payload);

        const purchaseRulePayload = getPurchaseRule(
            isExisting
                ? { ...response.data, ...pick(membership, ["purchaseRule", "redemptionPurchaseRule"]) }
                : response.data,
        );

        await api.put(`/purchaseRules/${response.data.purchaseRule!.id}`, purchaseRulePayload);

        return response;
    });

    const handleSubmit = async (values: MembershipDescriptionFormData) => {
        if (!values.enableMap && values.meetingLocation?.geo) {
            delete values.meetingLocation.geo;
        }

        const payload = omit(values, "collections");
        const { data } = await save(payload);
        const membershipId = data.id;

        const hasCollectionChanges =
            showCollectionModification &&
            values.collections &&
            !isEqual([...values.collections].sort(), [...addedCollectionPaths].sort());

        if (hasCollectionChanges) {
            await saveCollections({ newPaths: values.collections!, membershipId });
        }

        invalidate("products");
        invalidate("memberships", membership?.id);
        invalidate("purchaseRules", membership?.purchaseRule?.id);
        invalidate("forms");

        if (membership?.id) {
            flash.success("Successfully updated membership");
            return;
        }

        flash.success("Successfully created membership");

        history.push(`/products/memberships/${membershipId}/benefits`);
    };

    return (
        <ContentLayout isWide>
            <XolaFormik<MembershipDescriptionFormData>
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={getInitialValues(seller, membership, addedCollectionPaths)}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, errors, isSubmitting, dirty, handleSubmit, handleReset }) => {
                    return (
                        <>
                            <h2 className="mt-2">Description</h2>

                            <ContentLayout.Section title="Description">
                                {showCollectionModification && (
                                    <FormField name="collections" label="">
                                        <ProductCollectionSelector
                                            addedCollectionPaths={addedCollectionPaths}
                                            isLoading={isLoadingAddedCollections}
                                            value={values.collections ?? []}
                                            isOpen={collectionModal.isVisible}
                                            onChange={(collections: string[]) => {
                                                setFieldValue("collections", collections);
                                            }}
                                            onOpenChange={(isOpen) =>
                                                isOpen ? collectionModal.show() : collectionModal.hide()
                                            }
                                        />
                                    </FormField>
                                )}
                                <FormField name="name" label="Name">
                                    <InputField isRequired />
                                </FormField>

                                <FormField name="desc" label="Description">
                                    <TextareaField isRequired rows={6} />
                                </FormField>

                                <FormField name="price" label="Price">
                                    <CurrencyInputField filterZero isRequired isStyled />
                                </FormField>

                                <FormField name="refundPolicy" label="Refund Policy">
                                    <TextareaField rows={6} />
                                </FormField>

                                <div>
                                    <FormField name="enableMap" className="mb-3">
                                        <Switch.Group className="!items-start">
                                            <Switch
                                                size="medium"
                                                isChecked={values.enableMap}
                                                onChange={(checked: boolean) => setFieldValue("enableMap", checked)}
                                            />
                                            <Switch.Label direction="right" className="flex flex-col">
                                                <span className="font-bold">Meeting Location</span>
                                                <span>Enable and locate on map</span>
                                            </Switch.Label>
                                        </Switch.Group>
                                    </FormField>
                                    {!isLoading && (
                                        <LocationField
                                            isRequired={false}
                                            geo={values.meetingLocation?.geo}
                                            address={values.meetingLocation?.address ?? ""}
                                            handleAddressChange={(address: any) =>
                                                setFieldValue("meetingLocation.address", address)
                                            }
                                            handleLocationChange={(geo: Coordinates) =>
                                                setFieldValue("meetingLocation.geo", geo)
                                            }
                                            errors={errors.meetingLocation}
                                            enableMap={values.enableMap}
                                            setEnableMap={(value: boolean) => setFieldValue("enableMap", value)}
                                        />
                                    )}
                                </div>
                            </ContentLayout.Section>

                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton isLoading={isSubmitting} disabled={!dirty} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>

                                <SecondaryButton type="reset" disabled={!dirty} onClick={handleReset}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const validationSchema = object().shape({
    name: requiredString(),
    desc: requiredString(),
    refundPolicy: string(),
    price: requiredNumberMoreThanZero(),
    enableMap: boolean(),
    meetingLocation: object()
        .shape({
            address: string(),
        })
        .nullable(),
});

const getInitialValues = (seller: Seller, membership?: Membership, addedCollectionPaths: string[] = []) => {
    const isMapEnabled = !!membership?.meetingLocation?.geo?.lat;

    const formValues: MembershipDescriptionFormData = {
        ...getDefaultMembership(seller),
        ...omit(membership, ["triggers", "rewards"]),
        enableMap: isMapEnabled,
        ...(addedCollectionPaths.length > 0 ? { collections: addedCollectionPaths } : {}),
    };

    return formValues;
};

const getPurchaseRule = (membership: Membership): PurchaseRule => {
    const existingPurchaseRule = membership.purchaseRule;
    const actions: PurchaseRuleAction[] = [];
    const lineItemTemplateId = membership.catalog?.items.find((item) => item.type === CatalogType.QUANTITY)?.template
        ?.id;

    if (existingPurchaseRule && existingPurchaseRule.actions?.length > 0) {
        for (const action of existingPurchaseRule.actions) {
            if (
                action.object === PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION ||
                action.object === PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION
            ) {
                actions.push({
                    ...action,
                    _remove: true,
                });
            }
        }
    }

    if (lineItemTemplateId) {
        actions.push({
            object: PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
            filter: {
                object: PurchaseRuleFilterObject.EQUALS_FILTER,
                field: "template.id",
                operand: lineItemTemplateId,
            },
            shortcut: {
                field: "price",
                type: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
                amount: membership.price,
            },
        });
    }

    return { actions };
};
